import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, Space, Checkbox, Popover } from "antd";
import { SearchOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useMonitor } from "../context/MonitorContext";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS, DEFAULT_FILTER_STATUS, FLIGHT_STATUS_COLORS, FLIGHT_STATUS_ICONS, TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS, DEFAULT_TRANSACTION_FILTERS, TRANSACTION_FILTER_STATUS_LABELS } from "../constants";
import AircraftSelector from "../../../../components/common/AircraftSelector";

import { UTCDateRangePicker } from "../../../../components/DateTimeInput";
import TransactionFilterSelector from "./TransactionFilterSelector";
import { useAuth } from "../../../../hooks/useAuth";

const { Option } = Select;

/**
 * 统一航班过滤器组件
 *
 * 核心筛选逻辑与表单UI，供列表视图和紧凑视图（Drawer内）复用。
 * 支持：
 * - 自动查询（带防抖）
 * - 状态预设
 * - 事务筛选（权限控制）
 * - 全功能布局（Grid响应式）
 */
const UnifiedFlightFilter = () => {
    const [form] = Form.useForm();
    const { filters, updateFilters, fetchFlights, loading, airportsMap } = useMonitor();
    const { hasPermission } = useAuth();
    const searchDebounceRef = React.useRef(null);

    const [airportOptions, setAirportOptions] = useState([]);

    // UI状态，仅用于回显全选框的状态，不控制表单值
    const [indeterminate, setIndeterminate] = useState(true);
    const [checkAll, setCheckAll] = useState(false);

    // 检查用户是否有完整的航班运行权限（包括事务查看权限）
    const hasFullPermission = hasPermission("flight_operation:read");

    // 定义快速切换的状态组合
    const STATUS_PRESETS = {
        DEFAULT: {
            key: "default",
            label: "默认",
            values: DEFAULT_FILTER_STATUS,
            description: "计划、飞行中、延误",
        },
        WITH_COMPLETED: {
            key: "withCompleted",
            label: "含已完成",
            values: [...DEFAULT_FILTER_STATUS, FLIGHT_STATUS.COMPLETED],
            description: "默认状态 + 已完成",
        },
    };

    // 初始化机场列表
    useEffect(() => {
        if (airportsMap && Object.keys(airportsMap).length > 0) {
            const options = Object.entries(airportsMap).map(([code, airport]) => ({
                value: code,
                label: `${code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
            }));
            setAirportOptions(options);
        }
    }, [airportsMap]);

    // 更新全选/半选 UI 状态
    const updateCheckAllState = (status = []) => {
        const allStatusValues = Object.values(FLIGHT_STATUS);
        setIndeterminate(status.length > 0 && status.length < allStatusValues.length);
        setCheckAll(status.length === allStatusValues.length);
    };

    // 初始化表单值
    useEffect(() => {
        // 安全地处理 dateRange，确保使用UTC格式
        const safeDateRange = Array.isArray(filters.dateRange) && filters.dateRange.length >= 2 && filters.dateRange[0] ? [dayjs.utc(filters.dateRange[0]), dayjs.utc(filters.dateRange[1])] : null;

        const formValues = {
            status: filters.status,
            dateRange: safeDateRange,
            registrations: filters.registrations,
            searchText: filters.searchText,
            departure_airport: filters.departure_airport,
            arrival_airport: filters.arrival_airport,
            // 只有完整权限用户才设置事务筛选
            ...(hasFullPermission && { transactionFilters: filters.transactionFilters || DEFAULT_TRANSACTION_FILTERS }),
        };

        form.setFieldsValue(formValues);

        // 同步UI状态
        updateCheckAllState(filters.status || []);
    }, [form, filters, hasFullPermission]);

    // 提交筛选条件
    const submitFilters = (values) => {
        // 构建新的筛选条件
        const newFilters = {
            status: values.status || DEFAULT_FILTER_STATUS,
            dateRange: values.dateRange ? [values.dateRange[0].utc().format(), values.dateRange[1].utc().format()] : [null, null],
            registrations: values.registrations || [],
            searchText: values.searchText || "",
            departure_airport: values.departure_airport || "",
            arrival_airport: values.arrival_airport || "",
            // 只有完整权限用户才能使用事务筛选
            transactionFilters: hasFullPermission ? values.transactionFilters || DEFAULT_TRANSACTION_FILTERS : DEFAULT_TRANSACTION_FILTERS,
        };

        // 更新筛选条件
        updateFilters(newFilters);
    };

    // 表单值变化处理 (实时更新)
    const handleFormChange = (changedValues, allValues) => {
        // 如果是 searchText 变化，使用防抖
        if ("searchText" in changedValues) {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                submitFilters(allValues);
            }, 500);
            return;
        }

        // 如果是 status 变化，更新 UI 状态
        if ("status" in changedValues) {
            updateCheckAllState(changedValues.status || []);
        }

        // 其他字段立即提交
        submitFilters(allValues);
    };

    // 重置筛选条件
    const handleReset = () => {
        const today = dayjs.utc();
        const startDate = today.subtract(1, "day"); // D-1
        const endDate = today.add(6, "day"); // D+6

        const defaultFilters = {
            status: DEFAULT_FILTER_STATUS,
            dateRange: [startDate.format(), endDate.format()],
            registrations: [],
            searchText: "",
            departure_airport: "",
            arrival_airport: "",
            transactionFilters: DEFAULT_TRANSACTION_FILTERS,
        };

        // 设置表单
        form.setFieldsValue({
            status: defaultFilters.status,
            dateRange: [dayjs.utc(defaultFilters.dateRange[0]), dayjs.utc(defaultFilters.dateRange[1])],
            registrations: defaultFilters.registrations,
            searchText: defaultFilters.searchText,
            departure_airport: defaultFilters.departure_airport,
            arrival_airport: defaultFilters.arrival_airport,
            ...(hasFullPermission && { transactionFilters: defaultFilters.transactionFilters }),
        });

        // 更新 UI 状态
        updateCheckAllState(defaultFilters.status);

        // 更新 Context
        updateFilters(defaultFilters);
    };

    // 状态选项 - 使用常量，并加入图标和颜色
    const statusOptions = Object.keys(FLIGHT_STATUS).map((key) => {
        const statusValue = FLIGHT_STATUS[key];
        const labelText = FLIGHT_STATUS_LABELS[statusValue] || statusValue;
        const color = FLIGHT_STATUS_COLORS[statusValue] || FLIGHT_STATUS_COLORS.default;
        const IconComponent = FLIGHT_STATUS_ICONS[statusValue] || FLIGHT_STATUS_ICONS.default;

        return {
            value: statusValue,
            label: (
                <Space size="small">
                    {IconComponent}
                    <span style={{ color: color }}>{labelText}</span>
                </Space>
            ),
            rawLabel: labelText,
        };
    });

    // 安全地处理初始 dateRange，确保UTC格式
    const initialSafeDateRange = Array.isArray(filters.dateRange) && filters.dateRange.length >= 2 && filters.dateRange[0] ? [dayjs.utc(filters.dateRange[0]), dayjs.utc(filters.dateRange[1])] : null;

    // 全选处理
    const onCheckAllChange = (e) => {
        const checked = e.target.checked;
        const allStatusValues = Object.values(FLIGHT_STATUS);
        const newStatus = checked ? allStatusValues : [];

        // 更新表单
        form.setFieldsValue({ status: newStatus });
        // 更新 UI 状态
        updateCheckAllState(newStatus);
        // 触发更新
        handleFormChange({ status: newStatus }, form.getFieldsValue());
    };

    // 快速切换状态预设
    const handlePresetChange = (presetKey) => {
        const preset = Object.values(STATUS_PRESETS).find((p) => p.key === presetKey);
        if (preset) {
            form.setFieldsValue({ status: preset.values });
            updateCheckAllState(preset.values);
            handleFormChange({ status: preset.values }, form.getFieldsValue());
        }
    };

    // 获取当前激活的预设
    const getActivePreset = () => {
        const currentStatus = (form.getFieldValue("status") || []).slice().sort();
        for (const preset of Object.values(STATUS_PRESETS)) {
            if (JSON.stringify(preset.values.slice().sort()) === JSON.stringify(currentStatus)) {
                return preset.key;
            }
        }
        return null;
    };

    // 自定义下拉内容，添加全选选项
    const popupRender = (menu) => (
        <div>
            <div style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                <Checkbox indeterminate={indeterminate} checked={checkAll} onChange={onCheckAllChange} style={{ marginLeft: 8 }}>
                    全选
                </Checkbox>
            </div>
            {menu}
        </div>
    );

    // 检查是否有事务筛选条件（只有完整权限用户才检查）
    const hasTransactionFilters = hasFullPermission && filters.transactionFilters && Object.values(filters.transactionFilters).some((filterArray) => Array.isArray(filterArray) && filterArray.length > 0);

    // 计算事务筛选的统计信息（只有完整权限用户才计算）
    const getTransactionFilterSummary = () => {
        if (!hasFullPermission || !filters.transactionFilters) return { count: 0, summary: "" };

        let totalFilters = 0;
        const activeSummary = [];

        Object.entries(TRANSACTION_TYPES)
            .filter(([key, transactionType]) => transactionType !== TRANSACTION_TYPES.OCC_NOTE)
            .forEach(([key, transactionType]) => {
                const filterArray = filters.transactionFilters[transactionType];
                if (Array.isArray(filterArray) && filterArray.length > 0) {
                    totalFilters += filterArray.length;
                    const selectedStatusLabels = filterArray.map((status) => TRANSACTION_FILTER_STATUS_LABELS[status] || status);
                    activeSummary.push(`${TRANSACTION_TYPE_LABELS[transactionType]}: ${selectedStatusLabels.join(", ")}`);
                }
            });

        return {
            count: totalFilters,
            summary: activeSummary.join(", "),
            activeTypes: activeSummary.length,
        };
    };

    const transactionFilterSummary = getTransactionFilterSummary();

    // 事务筛选弹出框内容（只有完整权限用户才显示）
    const transactionFiltersContent = hasFullPermission ? (
        <div style={{ width: 300 }}>
            <Form.Item style={{ marginBottom: 0 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {Object.entries(TRANSACTION_TYPES)
                        .filter(([key, transactionType]) => transactionType !== TRANSACTION_TYPES.OCC_NOTE) // 过滤掉 OCC_NOTE
                        .map(([key, transactionType]) => (
                            <Form.Item key={transactionType} name={["transactionFilters", transactionType]} label={TRANSACTION_TYPE_LABELS[transactionType]} style={{ marginBottom: 0 }}>
                                <TransactionFilterSelector transactionType={transactionType} placeholder={`筛选${TRANSACTION_TYPE_LABELS[transactionType]}`} />
                            </Form.Item>
                        ))}
                </div>
            </Form.Item>
        </div>
    ) : null;

    return (
        <Form
            form={form}
            name="flightFilter"
            layout="vertical"
            onValuesChange={handleFormChange}
            // Removed outdated className that might cause confusion.
            // Styles are now handled by standard Ant Design grid and inline styles.
            initialValues={{
                status: filters.status,
                dateRange: initialSafeDateRange,
                registrations: filters.registrations,
                searchText: filters.searchText,
                departure_airport: filters.departure_airport,
                arrival_airport: filters.arrival_airport,
                // 只有完整权限用户才设置事务筛选初始值
                ...(hasFullPermission && { transactionFilters: filters.transactionFilters || DEFAULT_TRANSACTION_FILTERS }),
            }}
        >
            {/* 第一行 */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <div style={{ position: "relative", width: "100%" }}>
                        <Form.Item label="航班状态" name="status">
                            <Select mode="multiple" placeholder="选择航班状态" allowClear maxTagCount="responsive" optionFilterProp="rawLabel" filterOption={(input, option) => option && option.rawLabel && option.rawLabel.toLowerCase().includes(input.toLowerCase())} listHeight={300} popupRender={popupRender} style={{ width: "100%" }}>
                                {statusOptions.map((option) => (
                                    <Option key={option.value} value={option.value} rawLabel={option.rawLabel}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Space.Compact
                            size="small"
                            style={{
                                position: "absolute",
                                marginRight: "8px",
                                top: "0",
                                right: "0",
                                zIndex: 1,
                            }}
                        >
                            {Object.values(STATUS_PRESETS).map((preset) => (
                                <Button
                                    key={preset.key}
                                    type={getActivePreset() === preset.key ? "primary" : "default"}
                                    ghost={getActivePreset() === preset.key ? true : false}
                                    size="small"
                                    onClick={() => handlePresetChange(preset.key)}
                                    title={preset.description}
                                    style={{
                                        fontSize: "12px",
                                        height: "24px",
                                        padding: "0 8px",
                                        border: getActivePreset() !== preset.key ? "1px solid #d9d9d9" : undefined,
                                        color: getActivePreset() !== preset.key ? "#666" : undefined,
                                        marginRight: "1px",
                                    }}
                                >
                                    {preset.label}
                                </Button>
                            ))}
                        </Space.Compact>
                    </div>
                </Col>

                <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="dateRange" label="日期范围 (UTC)">
                        <UTCDateRangePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={["起始日期", "结束日期"]} />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="departure_airport" label="出发机场">
                        <Select showSearch placeholder="选择出发机场" allowClear optionFilterProp="label" filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())} options={airportOptions} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="arrival_airport" label="到达机场">
                        <Select showSearch placeholder="选择到达机场" allowClear optionFilterProp="label" filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())} options={airportOptions} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>

            {/* 第二行 */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="registrations" label="飞机注册号">
                        <AircraftSelector mode="multiple" placeholder="选择飞机注册号" maxTagCount="responsive" displayMode="simple" style={{ width: "100%" }} />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6} lg={6}>
                    <Form.Item name="searchText" label="搜索">
                        <Input placeholder="飞机注册号/航班号/机场/备注/航段(如:HKG-NQZ)/STC" prefix={<SearchOutlined />} allowClear style={{ width: "100%" }} />
                    </Form.Item>
                </Col>

                {/* 事务状态筛选 - 只有完整权限用户才显示 */}
                {hasFullPermission && (
                    <Col xs={24} sm={12} md={6} lg={6}>
                        <Form.Item label="事务状态筛选">
                            <Popover content={transactionFiltersContent} title="事务状态筛选（同时满足）" trigger="click" placement="bottomLeft" overlayStyle={{ width: 320 }}>
                                <Button
                                    icon={<SettingOutlined />}
                                    style={{
                                        width: "100%",
                                        color: hasTransactionFilters ? "#1890ff" : "#999",
                                        borderColor: hasTransactionFilters ? "#1890ff" : undefined,
                                    }}
                                    title={transactionFilterSummary.summary || "点击设置事务状态筛选"}
                                >
                                    {hasTransactionFilters ? (
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                            <span style={{ fontSize: "12px", textAlign: "left", flex: 1 }}>{transactionFilterSummary.summary}</span>
                                        </div>
                                    ) : (
                                        <span style={{ color: "#999" }}>点击添加事务筛选</span>
                                    )}
                                </Button>
                            </Popover>
                        </Form.Item>
                    </Col>
                )}

                <Col xs={24} sm={12} md={6} lg={hasFullPermission ? 6 : 12} style={{ textAlign: "right", alignSelf: "flex-end" }}>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button onClick={handleReset} icon={<ReloadOutlined />}>
                                重置
                            </Button>
                        </Space>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default UnifiedFlightFilter;
